import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

/**
 * Generic admin CRUD list.
 * Props:
 *  - title, subtitle, listEndpoint, createEndpoint, updateEndpoint(id), deleteEndpoint(id)
 *  - blank: default record
 *  - fields: [{ key, label, type:'text'|'textarea'|'select'|'checkbox'|'color'|'number'|'image', options? }]
 *  - renderCard: (item) => JSX for the visual card
 *  - testIdPrefix
 *  - visibilityKey (default 'is_visible')
 */
export default function AdminCMSList({
  title, subtitle, listEndpoint, createEndpoint, updateEndpoint, deleteEndpoint,
  blank, fields, renderCard, testIdPrefix, visibilityKey = "is_visible",
}) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get(listEndpoint).then((r) => setItems(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => { setEditing({ ...blank }); setOpen(true); };
  const startEdit = (it) => { setEditing({ ...it }); setOpen(true); };
  const save = async () => {
    const body = { ...editing };
    delete body.id; delete body.created_at; delete body.updated_at; delete body.votes;
    try {
      if (editing.id) await api.put(updateEndpoint(editing.id), body);
      else await api.post(createEndpoint, body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(deleteEndpoint(id)); toast.success("Deleted"); load(); };

  const toggleVisible = async (it) => {
    const body = { ...it, [visibilityKey]: !it[visibilityKey] };
    delete body.id; delete body.created_at; delete body.updated_at; delete body.votes;
    await api.put(updateEndpoint(it.id), body); load();
  };

  return (
    <div data-testid={testIdPrefix} className="space-y-8">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">{subtitle}</div>
          <h1 className="editorial-h1 text-5xl mt-2">{items.length} {title.toLowerCase()}.</h1>
        </div>
        <button data-testid={`${testIdPrefix}-new`} onClick={startNew} className="btn-pill btn-dark text-sm"><Plus size={16} /> New</button>
      </header>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((it) => (
          <div key={it.id} className="card-elevated overflow-hidden" data-testid={`${testIdPrefix}-card-${it.id}`}>
            {renderCard(it)}
            <div className="p-3 flex items-center justify-between gap-2 border-t border-brand-line">
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-charcoal/55">{it[visibilityKey] ? "Visible" : "Hidden"}</div>
              <div className="flex gap-2">
                <button onClick={() => toggleVisible(it)} className="btn-pill btn-ghost text-xs border border-brand-line" title="Toggle visibility">{it[visibilityKey] ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                <button data-testid={`${testIdPrefix}-edit-${it.id}`} onClick={() => startEdit(it)} className="btn-pill btn-ghost text-xs border border-brand-line">Edit</button>
                <button onClick={() => del(it.id)} className="btn-pill btn-ghost text-xs"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && editing && (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/40 grid place-items-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-3xl">{editing.id ? "Edit" : "New"} {title}</div>
              <button onClick={() => setOpen(false)}><X /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {fields.map((f) => (
                <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={f.rows || 3} className="pp-input" value={editing[f.key] ?? ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} data-testid={`${testIdPrefix}-f-${f.key}`} />
                  ) : f.type === "select" ? (
                    <select className="pp-input bg-transparent" value={editing[f.key] ?? ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} data-testid={`${testIdPrefix}-f-${f.key}`}>
                      {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : f.type === "checkbox" ? (
                    <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={!!editing[f.key]} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })} data-testid={`${testIdPrefix}-f-${f.key}`} /> {f.help || "Yes"}</label>
                  ) : f.type === "color" ? (
                    <input type="color" className="h-10 w-16 border border-brand-line rounded-md mt-1" value={editing[f.key] || "#FFC93C"} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} data-testid={`${testIdPrefix}-f-${f.key}`} />
                  ) : f.type === "number" ? (
                    <input type="number" className="pp-input" value={editing[f.key] ?? 0} onChange={(e) => setEditing({ ...editing, [f.key]: Number(e.target.value) || 0 })} data-testid={`${testIdPrefix}-f-${f.key}`} />
                  ) : f.type === "tags" ? (
                    <input className="pp-input" placeholder="Comma-separated" value={Array.isArray(editing[f.key]) ? editing[f.key].join(", ") : (editing[f.key] ?? "")} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} data-testid={`${testIdPrefix}-f-${f.key}`} />
                  ) : (
                    <input className="pp-input" value={editing[f.key] ?? ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} data-testid={`${testIdPrefix}-f-${f.key}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-8 flex justify-end gap-3"><button onClick={() => setOpen(false)} className="btn-pill btn-ghost text-sm">Cancel</button><button onClick={save} data-testid={`${testIdPrefix}-f-save`} className="btn-pill btn-dark text-sm">Save</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
